import React from 'react';
import { useLanguage } from '../i18n';

export default function TermsPage() {
  const { t } = useLanguage();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '2rem 1rem' }}>
      <div style={{ maxWidth: '48rem', margin: '0 auto', backgroundColor: '#FFFFFF', borderRadius: '1rem', padding: '3rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '800', color: '#1A1A1A', marginBottom: '0.5rem' }}>Terms of Service</h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Last updated: July 2026 · Veyl</p>
        </div>

        {/* Translation Notice */}
        <div style={{
          backgroundColor: 'rgba(27,122,138,0.08)',
          border: '1px solid #1B7A8A',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '2rem',
          fontSize: '0.875rem',
          color: '#1B7A8A'
        }}>
          🌐 {t('legalEnglishOnly')}
        </div>

        {[
          {
            title: '1. Acceptance of Terms',
            content: `By creating an account and using Veyl, you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the service.`
          },
          {
            title: '2. Age Eligibility',
            content: `Veyl is available only to individuals who are 18 years of age or older. There is no version of Veyl available to minors, and no account tier, feature, or matching pool is designed for or intended to be used by anyone under 18. By creating an account, you confirm that you are 18 years of age or older and that the age information you provide during onboarding is accurate. Misrepresenting your age to access Veyl is a violation of these Terms. If Veyl determines, at its sole discretion, that a user misrepresented their age at signup, that user's account will be permanently banned without warning. Depending on the circumstances, Veyl may also pursue legal action or report the matter to relevant authorities. Veyl relies on the accuracy of user-provided age information and does not independently verify age through third-party identity verification. Users are solely responsible for the accuracy of the age they provide, and Veyl disclaims liability arising from a user's misrepresentation of their age.`
          },
          {
            title: '3. Description of Service',
            content: `Veyl is an anonymous accountability platform that matches users with accountability partners to help them work toward personal goals. The service includes text messaging, voice notes, group chats, community chats, check-ins, and goal tracking features. Veyl is provided by Veyl, a service operated from Kenya.`
          },
          {
            title: '4. User Accounts',
            content: `You are responsible for maintaining the confidentiality of your account. You agree to provide accurate information during onboarding and to use a pseudonym rather than your real name to protect your anonymity. You are solely responsible for all activity that occurs under your account.`
          },
          {
            title: '5. Acceptable Use',
            content: `You agree not to use Veyl to harass, threaten, or harm other users. You agree not to share personal identifying information about other users. You agree not to use the platform for any illegal purpose. You agree not to attempt to reverse-engineer or exploit the platform. Violations may result in immediate account suspension or permanent ban.`
          },
          {
            title: '6. Anonymous Matching',
            content: `Veyl matches you with accountability partners based on goal category and timezone. You will interact under anonymous pseudonyms and avatars. You must not attempt to identify, locate, or contact your partner outside of Veyl. Sharing or soliciting personal contact information is a violation of these terms.`
          },
          {
            title: '7. Voice Notes & Content',
            content: `Voice notes are stored temporarily and may be subject to storage limits. Voice notes include optional pitch modulation for additional anonymity. All content you submit through Veyl, including messages and voice notes, must comply with these terms. Veyl reserves the right to remove content that violates these terms or applicable law.`
          },
          {
            title: '8. Reporting & Moderation',
            content: `Veyl has a strike-based system for handling reports of misconduct. Users who receive multiple confirmed reports may have their accounts suspended or permanently disabled. You may report and block users directly from the chat interface. We reserve the right to make moderation decisions at our discretion to protect the safety of our community.`
          },
          {
            title: '9. Free Tier Limits',
            content: `Free tier users are subject to daily usage limits including 10 text messages per day, 5 voice notes per day, 20 group messages per day, and 30 community messages per day. These limits reset daily. Upgrading to Premium removes all daily limits.`
          },
          {
            title: '10. Subscription & Payments',
            content: `Veyl offers a free tier and a paid Premium subscription at $5/month or $20 for 6 months. Premium features include unlimited messages, unlimited voice notes, and priority matching. Payments are processed securely through our payment provider, Polar. Refunds are handled on a case-by-case basis — contact support@veyl.space within 7 days of a charge if you believe an error occurred.`
          },
          {
            title: '11. Account Deletion',
            content: `You may delete your account at any time through the Settings page. Upon deletion, your account data including messages, check-ins, voice notes, and Veyls will be permanently and irreversibly removed. Your accountability partner will be returned to the matching pool. This action cannot be undone.`
          },
          {
            title: '12. Termination by Veyl',
            content: `Veyl reserves the right to terminate or suspend accounts that violate these terms without prior notice. In cases of serious violations such as harassment, abuse, or illegal activity, accounts may be permanently banned with no possibility of appeal.`
          },
          {
            title: '13. Disclaimer of Warranties',
            content: `Veyl is provided "as is" without warranties of any kind. We do not guarantee that the service will be uninterrupted, error-free, or that matches will be successful. We are not responsible for the conduct of any user on the platform. Accountability outcomes depend entirely on the effort and commitment of individual users.`
          },
          {
            title: '14. Limitation of Liability',
            content: `To the maximum extent permitted by Kenyan law, Veyl shall not be liable for any indirect, incidental, or consequential damages arising from your use of the service. Our total liability shall not exceed the amount you paid us in the 3 months preceding any claim.`
          },
          {
            title: '15. Changes to Terms',
            content: `We may update these terms from time to time. We will notify users of significant changes via the app or email. Continued use of Veyl after changes constitutes acceptance of the updated terms.`
          },
          {
            title: '16. Governing Law',
            content: `These Terms of Service are governed by the laws of Kenya. Any disputes arising from these terms or your use of Veyl shall be subject to the jurisdiction of Kenyan courts.`
          },
          {
            title: '17. Contact',
            content: `For questions about these Terms of Service, contact us at support@veyl.space.`
          },
        ].map(section => (
          <div key={section.title} style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1A1A1A', marginBottom: '0.75rem' }}>
              {section.title}
            </h2>
            <p style={{ color: '#4b5563', lineHeight: '1.8', fontSize: '0.95rem' }}>
              {section.content}
            </p>
          </div>
        ))}

        <div style={{ marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
          <a href="/" style={{ color: '#1B7A8A', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>← Back to Veyl</a>
        </div>
      </div>
    </div>
  );
}